import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Table,
  Badge,
  Button,
  Modal,
  ModalBody,
} from 'reactstrap';
import { useDetailQuery } from 'http-client/hooks/useCrud';
import { API_ENDPOINTS } from 'http-client/httpConstants';
import { SellInvoice, SellItem } from '../../Sales/Invoices/types';
import './SellInvoiceDetail.css';
import { fmt, fmtDate } from '../../../utils/common';
import { StatusBadge } from '../../../components/Common/StatusBadge';
import { DeltaChip } from '../../../components/Common/DeltaChip';
import CreateSellAdjustment from '../../../components/Form/Tenant/Adjustments/SellAdjustments/CreateSellAdjustment';
import { Adjustment } from '@/types/adjustment.types';

// ─── Types ────────────────────────────────────────────────────────────────────

interface AdjustmentItem {
  id: string;
  shopItemId: string;
  originalQuantity: number;
  quantityDelta: number;
  originalUnitPrice: number;
  priceDelta: number;
  discountDelta: number;
  notes?: string;
  shopItem?: { name: string };
}

interface EnrichedSellInvoice extends SellInvoice {
  adjustments?: Adjustment[];
  amountPaid?: number;
  changeReturned?: number | null;
  saleType?: 'WalkIn' | 'Delivery';
  stage?: 'Received' | 'Processed' | 'Shipped' | 'Delivered';
  deliveryAddress?: string | null;
  deliveryContact?: string | null;
  remarks?: string | null;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const adjTypeLabel: Record<string, string> = {
  QuantityChange: 'Quantity change',
  PriceChange: 'Price change',
  ItemRemoval: 'Item removal',
  DiscountChange: 'Discount change',
};

const adjTypeColor: Record<string, string> = {
  QuantityChange: 'primary',
  PriceChange: 'warning',
  ItemRemoval: 'danger',
  DiscountChange: 'success',
};

const paymentMethodIcon: Record<string, string> = {
  Cash: 'bx-money',
  Card: 'bx-credit-card',
  'Bank Transfer': 'bx-transfer',
  Credit: 'bx-time',
};

const stageColor: Record<string, string> = {
  Received: 'info',
  Processed: 'primary',
  Shipped: 'warning',
  Delivered: 'success',
};

const saleTypeIcon: Record<string, string> = {
  WalkIn: 'bx-walk',
  Delivery: 'bx-truck',
};

const QuantityDelta: React.FC<{ delta: number }> = ({ delta }) => (
  <span className={`quantity-delta delta-${delta > 0 ? 'inc' : delta < 0 ? 'dec' : 'none'}`}>
    {delta > 0 ? '+' : ''}
    {delta}
  </span>
);

const PriceDelta: React.FC<{ delta: number }> = ({ delta }) => (
  <span className={`price-delta delta-${delta > 0 ? 'inc' : delta < 0 ? 'dec' : 'none'}`}>
    {delta > 0 ? '+' : ''}
    {fmt(delta)}
  </span>
);

const AdjustmentItemRow: React.FC<{ item: AdjustmentItem }> = ({ item }) => (
  <tr>
    <td>
      <div className="item-name">{item.shopItem?.name ?? item.shopItemId}</div>
    </td>
    <td className="text-end mono">{item.originalQuantity}</td>
    <td className="text-end">
      <QuantityDelta delta={item.quantityDelta} />
    </td>
    <td className="text-end mono">{fmt(item.originalUnitPrice)}</td>
    <td className="text-end">
      <PriceDelta delta={item.priceDelta} />
    </td>
    <td className="text-end mono">{fmt(item.discountDelta)}</td>
  </tr>
);

const AdjustmentCard: React.FC<{ adj: Adjustment; index: number }> = ({ adj, index }) => {
  const [open, setOpen] = useState(index === 0);

  return (
    <Card className="adjustment-card mb-2">
      <div
        className="adjustment-header"
        onClick={() => setOpen(!open)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') setOpen(!open);
        }}
      >
        <div className="d-flex align-items-center gap-2">
          <Badge color={adjTypeColor[adj.adjustmentType]} pill className="adjustment-type">
            {adjTypeLabel[adj.adjustmentType] ?? adj.adjustmentType}
          </Badge>
          <span className="adjustment-date">{fmtDate(adj.createdAt)}</span>
          {adj.reason && <span className="adjustment-reason-preview">— {adj.reason}</span>}
        </div>
        <div className="d-flex align-items-center gap-3">
          <DeltaChip value={adj.netAmountDelta} />
          <i className={`bx bx-chevron-down adjustment-chevron ${open ? 'open' : ''}`}></i>
        </div>
      </div>
      <div className={`adjustment-body ${open ? 'open' : ''}`}>
        <div className="adjustment-body-inner">
          <Table hover responsive size="sm" className="adjustment-table">
            <thead>
              <tr>
                <th>Item</th>
                <th className="text-end">Orig. qty (Pieces)</th>
                <th className="text-end">Qty(Pieces) delta</th>
                <th className="text-end">Orig. price</th>
                <th className="text-end">Price delta</th>
                <th className="text-end">Disc. delta</th>
              </tr>
            </thead>
            <tbody>
              {adj.items.map((item) => (
                <AdjustmentItemRow key={item.id} item={item} />
              ))}
            </tbody>
          </Table>
          {adj.reason && (
            <div className="adjustment-reason">
              <strong>Reason:</strong> {adj.reason}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};

// ─── Skeleton Components ─────────────────────────────────────────────────────

const SkeletonRow: React.FC = () => (
  <tr className="skeleton-row">
    <td>
      <div className="skeleton-text" style={{ width: 40 }}></div>
    </td>
    <td>
      <div className="skeleton-text" style={{ width: 120 }}></div>
    </td>
    <td>
      <div className="skeleton-text" style={{ width: 80 }}></div>
    </td>
    <td>
      <div className="skeleton-text" style={{ width: 60 }}></div>
    </td>
    <td>
      <div className="skeleton-text" style={{ width: 70 }}></div>
    </td>
    <td>
      <div className="skeleton-text" style={{ width: 60 }}></div>
    </td>
    <td>
      <div className="skeleton-text" style={{ width: 70 }}></div>
    </td>
    <td>
      <div className="skeleton-text" style={{ width: 80 }}></div>
    </td>
  </tr>
);

const SkeletonHero: React.FC = () => (
  <Card className="hero-card skeleton">
    <CardBody className="d-flex justify-content-between align-items-center">
      <div>
        <div className="skeleton-text skeleton-title mb-2" style={{ width: 200 }}></div>
        <div className="d-flex gap-3">
          <div className="skeleton-text" style={{ width: 100 }}></div>
          <div className="skeleton-text" style={{ width: 120 }}></div>
          <div className="skeleton-text" style={{ width: 80 }}></div>
        </div>
      </div>
      <div className="skeleton-badge" style={{ width: 80, height: 26 }}></div>
    </CardBody>
  </Card>
);

const SkeletonInfoCard: React.FC = () => (
  <Card className="skeleton">
    <CardHeader>
      <div className="skeleton-text" style={{ width: 100 }}></div>
    </CardHeader>
    <CardBody>
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="skeleton-kv mb-3">
          <div className="skeleton-text" style={{ width: 60 }}></div>
          <div className="skeleton-text" style={{ width: 120 }}></div>
        </div>
      ))}
    </CardBody>
  </Card>
);

const SkeletonTable: React.FC = () => (
  <Card className="skeleton">
    <CardHeader>
      <div className="skeleton-text" style={{ width: 80 }}></div>
    </CardHeader>
    <div className="table-responsive">
      <Table className="skeleton-table">
        <thead>
          <tr>
            <th>#</th>
            <th>Medicine</th>
            <th className="text-end">Packs</th>
            <th className="text-end">Rate</th>
            <th className="text-end">Tax</th>
            <th className="text-end">Discount</th>
            <th className="text-end">Net value</th>
          </tr>
        </thead>
        <tbody>
          {[1, 2, 3].map((i) => (
            <SkeletonRow key={i} />
          ))}
        </tbody>
      </Table>
    </div>
  </Card>
);

// ─── Main Component ──────────────────────────────────────────────────────────

const SellInvoiceDetailPage: React.FC = () => {
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const navigate = useNavigate();
  const [isAdjustmentModalOpen, setIsAdjustmentModalOpen] = useState(false);

  const { data, isLoading, isError } = useDetailQuery<EnrichedSellInvoice>(
    API_ENDPOINTS.TENANT.SELLS.WITH_ID,
    { id: invoiceId! },
  );

  const [invoice, setInvoice] = useState<EnrichedSellInvoice | null>(null);

  useEffect(() => {
    if (data) setInvoice(data);
  }, [data]);

  // totalDiscount by sum on each item discount
  const totalItemDiscount =
    invoice?.items.reduce((sum, item) => sum + (item.discountAmount ?? 0), 0) ?? 0;
  // total tax by sum on each item tax
  const totalItemTax = invoice?.items.reduce((sum, item) => sum + (item.taxAmount ?? 0), 0) ?? 0;

  const totalDiscount = totalItemDiscount + (invoice?.discount ?? 0);
  const totalTax = totalItemTax + (invoice?.taxAmount ?? 0);
  const hasAdj = (invoice?.adjustments?.length ?? 0) > 0;
  const netAfterAdj = hasAdj
    ? (invoice?.totalAmount ?? 0) +
      (invoice?.adjustments?.reduce((s, a) => s + a.netAmountDelta, 0) ?? 0)
    : null;

  const remainingAmount = invoice?.totalAmount
    ? invoice.totalAmount - (invoice.amountPaid ?? 0)
    : 0;

  const printInvoice = () => window.print();

  const openAdjustmentModal = () => setIsAdjustmentModalOpen(true);
  const closeAdjustmentModal = () => setIsAdjustmentModalOpen(false);

  // Determine payment status based on amount paid
  const getPaymentStatus = () => {
    if (!invoice) return 'Pending';
    if (invoice.amountPaid === 0) return 'Pending';
    if (invoice.amountPaid >= invoice.totalAmount) return 'Paid';
    return 'Partial';
  };

  const paymentStatus = getPaymentStatus();
  const paymentStatusColor = {
    Paid: 'success',
    Partial: 'warning',
    Pending: 'danger',
  };

  if (isError) {
    return (
      <Container className="py-5 text-center">
        <i className="bx bx-error-circle error-icon"></i>
        <h5 className="mt-3 text-muted">Failed to load invoice</h5>
        <Button color="primary" onClick={() => window.location.reload()} className="mt-3">
          <i className="bx bx-revision"></i> Retry
        </Button>
      </Container>
    );
  }

  return (
    <div className="invoice-detail-page">
      {/* Header */}
      <div className="page-header">
        <Container fluid className="px-4">
          <div className="d-flex justify-content-between align-items-center">
            <div className="breadcrumb-nav">
              <button className="breadcrumb-link" onClick={() => navigate(-1)}>
                Sales
              </button>
              <i className="bx bx-chevron-right"></i>
              <span className="breadcrumb-current">
                {isLoading ? 'Loading...' : invoice?.invoiceNumber ?? 'Invoice detail'}
              </span>
            </div>
            <div className="header-actions">
              {!isLoading && !isError && invoice?.status === 'Posted' && (
                <Button outline size="sm" onClick={openAdjustmentModal}>
                  <i className="bx bx-plus"></i> Create adjustment
                </Button>
              )}
              <Button outline size="sm" onClick={printInvoice}>
                <i className="bx bx-printer"></i> Print
              </Button>
              <Button outline size="sm" onClick={() => navigate(-1)}>
                <i className="bx bx-arrow-back"></i> Back
              </Button>
            </div>
          </div>
        </Container>
      </div>

      <Container className="page-body">
        {isLoading ? (
          <div className="skeleton-container">
            <SkeletonHero />
            <Row className="mt-4">
              <Col md={6}>
                <SkeletonInfoCard />
              </Col>
              <Col md={6}>
                <SkeletonInfoCard />
              </Col>
            </Row>
            <SkeletonTable />
          </div>
        ) : invoice ? (
          <div className="content-container">
            {/* Hero Card */}
            <Card className="hero-card mb-4">
              <CardBody>
                <div className="hero-main d-flex justify-content-between align-items-start gap-3 flex-wrap">
                  <div>
                    <div className="hero-title-group">
                      <h2 className="invoice-number">{invoice.invoiceNumber}</h2>
                      <StatusBadge status={invoice.status} />
                      <Badge color={paymentStatusColor[paymentStatus]} pill>
                        {paymentStatus}
                      </Badge>
                      <Badge color={stageColor[invoice.stage || 'Received']} pill>
                        {invoice.stage || 'Received'}
                      </Badge>
                      {hasAdj && (
                        <Badge color="light" pill className="adjustment-count">
                          {invoice.adjustments!.length} adjustment
                          {invoice.adjustments!.length > 1 ? 's' : ''}
                        </Badge>
                      )}
                    </div>
                    <div className="hero-meta">
                      <span>
                        <i className="bx bx-calendar"></i> {fmtDate(invoice.invoiceDate)}
                      </span>
                      <span>
                        <i className={`bx ${saleTypeIcon[invoice.saleType || 'WalkIn']}`}></i>{' '}
                        {invoice.saleType || 'WalkIn'}
                      </span>
                      {invoice.paymentMethod && (
                        <span>
                          <i className={`bx ${paymentMethodIcon[invoice.paymentMethod]}`}></i>{' '}
                          {invoice.paymentMethod}
                        </span>
                      )}
                      <span className="total-amount">
                        <i className="bx bx-receipt"></i> {fmt(invoice.totalAmount)}
                      </span>
                    </div>
                  </div>

                  <div className="d-flex gap-2 align-items-center flex-wrap">
                    {invoice.status === 'Posted' && (
                      <Button color="primary" size="sm" onClick={openAdjustmentModal}>
                        <i className="bx bx-plus me-1"></i> Create Adjustment
                      </Button>
                    )}
                    <Button outline size="sm" onClick={printInvoice}>
                      <i className="bx bx-printer me-1"></i> Print
                    </Button>
                    <Button outline size="sm" onClick={() => navigate(-1)}>
                      <i className="bx bx-arrow-back me-1"></i> Back
                    </Button>
                  </div>

                  {netAfterAdj !== null && (
                    <div className="text-end">
                      <div className="text-muted small">Effective total</div>
                      <div
                        className={`fw-bold ${
                          netAfterAdj < invoice.totalAmount ? 'text-success' : 'text-danger'
                        }`}
                      >
                        {fmt(netAfterAdj)}
                      </div>
                      <div className="text-muted small">after adjustments</div>
                    </div>
                  )}
                </div>
              </CardBody>
            </Card>

            {/* Info Cards Row */}
            <Row className="mb-4">
              <Col md={6}>
                <Card>
                  <CardHeader>
                    <CardTitle tag="h5">
                      <i className="bx bx-info-circle"></i> Sale Information
                    </CardTitle>
                  </CardHeader>
                  <CardBody>
                    <div className="info-row">
                      <span className="info-label">Sale type</span>
                      <span>{invoice.saleType || 'WalkIn'}</span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">Stage</span>
                      <Badge color={stageColor[invoice.stage || 'Received']}>
                        {invoice.stage || 'Received'}
                      </Badge>
                    </div>
                    <div className="info-row">
                      <span className="info-label">Remarks</span>
                      <span>{invoice.remarks || '—'}</span>
                    </div>
                    {invoice.saleType === 'Delivery' && (
                      <>
                        <div className="info-row">
                          <span className="info-label">Delivery address</span>
                          <span>{invoice.deliveryAddress || '—'}</span>
                        </div>
                        <div className="info-row">
                          <span className="info-label">Delivery contact</span>
                          <span>{invoice.deliveryContact || '—'}</span>
                        </div>
                      </>
                    )}
                  </CardBody>
                </Card>
              </Col>
              <Col md={6}>
                <Card>
                  <CardHeader>
                    <CardTitle tag="h5">
                      <i className="bx bx-credit-card"></i> Payment Details
                    </CardTitle>
                  </CardHeader>
                  <CardBody>
                    <div className="info-row">
                      <span className="info-label">Payment method</span>
                      <span>{invoice.paymentMethod || '—'}</span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">Payment status</span>
                      <Badge color={paymentStatusColor[paymentStatus]}>{paymentStatus}</Badge>
                    </div>
                    <div className="info-row">
                      <span className="info-label">Amount paid</span>
                      <span className="text-success fw-bold mono">
                        {fmt(invoice.amountPaid ?? 0)}
                      </span>
                    </div>
                    {invoice.changeReturned !== null &&
                      invoice.changeReturned !== undefined &&
                      invoice.changeReturned > 0 && (
                        <div className="info-row">
                          <span className="info-label">Change returned</span>
                          <span className="fw-bold mono">{fmt(invoice.changeReturned)}</span>
                        </div>
                      )}
                    <div className="info-row">
                      <span className="info-label">Remaining amount</span>
                      <span
                        className={`fw-bold mono ${
                          remainingAmount > 0 ? 'text-danger' : 'text-success'
                        }`}
                      >
                        {fmt(remainingAmount)}
                      </span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">Invoice date</span>
                      <span>{fmtDate(invoice.invoiceDate)}</span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">Created at</span>
                      <span>{fmtDate(invoice.createdAt)}</span>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>

            {/* Items Table */}
            <Card className="mb-4">
              <CardHeader className="d-flex justify-content-between align-items-center">
                <CardTitle tag="h5">
                  <i className="bx bx-package"></i> Items ({invoice.items.length})
                </CardTitle>
                <span className="text-muted small">
                  {invoice.items.length} line item{invoice.items.length !== 1 ? 's' : ''}
                </span>
              </CardHeader>
              <div className="table-responsive">
                <Table hover className="items-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Medicine</th>
                      <th className="text-end">Quantity</th>
                      <th className="text-end">Unit Price</th>
                      <th className="text-end">Tax</th>
                      <th className="text-end">Discount</th>
                      <th className="text-end">Net value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoice.items.map((item, idx) => (
                      <tr key={item.id}>
                        <td>
                          <span className="seq-number">{idx + 1}</span>
                        </td>
                        <td>
                          <div className="item-name">{item.itemName}</div>
                        </td>
                        <td className="text-end fw-bold">{item.quantity}</td>
                        <td className="text-end mono">{fmt(item.unitPrice)}</td>
                        <td className="text-end mono">{fmt(item.taxAmount ?? 0)}</td>
                        <td className="text-end mono">{fmt(item.discountAmount ?? 0)}</td>
                        <td className="text-end fw-bold">{fmt(item.netValue)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={4}></td>
                      <td className="text-end text-primary small fw-bold">Tax: {fmt(totalTax)}</td>
                      <td className="text-end small fw-bold text-success">
                        Disc: {fmt(totalDiscount)}
                      </td>
                      <td className="text-end fw-bold mono text-danger">
                        {fmt(invoice.totalAmount)}
                      </td>
                    </tr>
                  </tfoot>
                </Table>
              </div>
              <CardBody className="border-top">
                <div className="totals-box">
                  <div className="total-row text-danger fw-bold">
                    <span>Subtotal</span>
                    <span>{fmt(invoice.subTotal)}</span>
                  </div>
                  <div className="total-row text-success fw-bold">
                    <span>Discount</span>
                    <span>−{fmt(totalDiscount)}</span>
                  </div>
                  <div className="total-row text-primary fw-bold">
                    <span>Tax</span>
                    <span>{fmt(totalTax)}</span>
                  </div>
                  <div className="total-row-divider"></div>
                  <div className="total-row highlight">
                    <span>Invoice total</span>
                    <span className="mono">{fmt(invoice.totalAmount)}</span>
                  </div>
                  <div className="total-row">
                    <span>Amount paid</span>
                    <span className="text-success mono">{fmt(invoice.amountPaid ?? 0)}</span>
                  </div>
                  {invoice.changeReturned !== null &&
                    invoice.changeReturned !== undefined &&
                    invoice.changeReturned > 0 && (
                      <div className="total-row">
                        <span>Change returned</span>
                        <span className="text-warning mono">{fmt(invoice.changeReturned)}</span>
                      </div>
                    )}
                  {remainingAmount > 0 && (
                    <div className="total-row">
                      <span>Remaining</span>
                      <span className="text-danger mono">{fmt(remainingAmount)}</span>
                    </div>
                  )}
                  {netAfterAdj !== null && (
                    <div className="total-row effective">
                      <span>Effective (after adj.)</span>
                      <span
                        className={
                          netAfterAdj < invoice.totalAmount ? 'text-success' : 'text-danger'
                        }
                      >
                        {fmt(netAfterAdj)}
                      </span>
                    </div>
                  )}
                </div>
              </CardBody>
            </Card>

            {/* Adjustments Section */}
            {hasAdj && (
              <Card className="mb-4">
                <CardHeader>
                  <CardTitle tag="h5">
                    <i className="bx bx-adjust"></i> Adjustments
                  </CardTitle>
                  <span className="text-muted small">
                    Original invoice unchanged — corrections listed below
                  </span>
                </CardHeader>
                <CardBody>
                  {invoice.adjustments!.map((adj, i) => (
                    <AdjustmentCard key={adj.id} adj={adj} index={i} />
                  ))}
                </CardBody>
              </Card>
            )}

            {/* No Adjustments CTA */}
            {!hasAdj && invoice.status === 'Posted' && (
              <Card className="text-center py-5">
                <CardBody>
                  <i className="bx bx-info-circle fs-1 text-muted"></i>
                  <p className="mt-3 text-muted">No adjustments on this invoice.</p>
                  <Button color="primary" onClick={openAdjustmentModal}>
                    <i className="bx bx-plus"></i> Create adjustment
                  </Button>
                </CardBody>
              </Card>
            )}
          </div>
        ) : null}
      </Container>

      {/* Adjustment Modal */}
      <Modal
        isOpen={isAdjustmentModalOpen}
        toggle={closeAdjustmentModal}
        size="xl"
        backdrop="static"
        className="adjustment-modal"
      >
        <ModalBody className="p-0">
          <CreateSellAdjustment invoiceId={invoiceId!} onClose={closeAdjustmentModal} />
        </ModalBody>
      </Modal>
    </div>
  );
};

export default SellInvoiceDetailPage;
